import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { loginUser } from '../../services/auth';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setTheme } = useTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const validate = () => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setApiError('');

    try {
      const response :any= await loginUser({ email, password });

      // Save token or identifier
      localStorage.setItem('authToken', response.data.token || 'dummy-token');
      localStorage.setItem('ud-user', response.data._id || 'dummy-token');
      localStorage.setItem('user', JSON.stringify(response.data));
      // Update ThemeContext immediately so sidebar shows correct logo/name
      setTheme(response.data);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error: any) {
      const apiErrorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      setApiError(apiErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white rounded-2xl shadow-lg flex max-w-4xl w-full">
        {/* Left Panel - Logo */}
        <div className="w-1/2 bg-gradient-to-br from-green-400 to-green-600 text-white flex flex-col items-center justify-center rounded-l-2xl p-8">
          <img src={require('../../assets/logo.png')} alt="Logo" className="w-40 h-40 object-contain mb-4" />
          <h2 className="text-2xl font-bold">Welcome Back!</h2>
          <p className="text-center mt-2">Log in to access your dashboard</p>
        </div>

        {/* Right Panel - Form */}
        <div className="w-1/2 p-8">
          <h2 className="text-3xl font-semibold text-gray-800 mb-6">Login</h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* API Error Message */}
            {apiError && (
              <div className="bg-red-100 text-red-700 text-sm p-3 rounded-md text-center">
                {apiError}
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="block mb-1 text-gray-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-2 border ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400`}
                placeholder="Enter your email"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* Password Field */}
            <div className="relative">
              <label className="block mb-1 text-gray-600">Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-2 border ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="absolute top-10 right-3 flex items-center text-gray-500"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition duration-200"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>

            {/* Forgot Password */}
            <div className="text-center mt-3">
              <Link to="/forgot" className="text-sm text-green-600 hover:underline">
                Forgot Password?
              </Link>
            </div>

            {/* Signup */}
            <div className="text-center mt-3">
              <p className="text-sm">
                Don't have an account?{' '}
                <Link to="/signup" className="text-green-600 hover:underline">
                  Signup
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
